import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, CalendarDays, MapPin, Tag, X, ZoomIn } from "lucide-react";
import { Reveal } from "./Reveal";
import { EVENTS, isPastEvent } from "../lib/events";
import { useLang, usePath } from "../i18n";

export const Exhibitions = ({ h1 = false, showPast = true }) => {
  const { t } = useLang();
  const lp = usePath();
  const E = t.events;
  const H = h1 ? "h1" : "h2";
  const [zoom, setZoom] = useState(null); // { src, alt }

  useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => e.key === "Escape" && setZoom(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom]);

  // Automatic split: an event joins "Past exhibitions" the day after it ends.
  const upcoming = EVENTS.filter((ev) => !isPastEvent(ev)).sort((a, b) => new Date(a.end) - new Date(b.end));
  const past = EVENTS.filter(isPastEvent).sort((a, b) => new Date(b.end) - new Date(a.end));
  return (
    <section data-testid="exhibitions" className="bg-clara-espresso text-clara-cream noise-overlay relative border-t border-clara-cream/10 py-24">
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10">
        <Reveal>
          <p className="eyebrow text-clara-terracotta mb-4">{E.eyebrow}</p>
          <H className="font-display font-light text-4xl lg:text-6xl tracking-tighter mb-14">{E.title}</H>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-px bg-clara-cream/15 border border-clara-cream/15 mb-10">
          {upcoming.map((ev, i) => (
            <Reveal key={ev.id} delay={i * 0.08} className="bg-clara-espresso">
              <div className="h-full flex flex-col" data-testid={`event-${ev.id}`}>
                {ev.photo && (
                  <button
                    type="button"
                    onClick={() => setZoom({ src: ev.photo, alt: `Clara booth at ${ev.name}` })}
                    data-testid={`event-photo-${ev.id}`}
                    aria-label={`Enlarge photo: ${ev.name}`}
                    className="group/photo relative block w-full aspect-[16/9] overflow-hidden border-b border-clara-cream/15 cursor-zoom-in"
                  >
                    <img src={ev.photo} alt={`Clara booth at ${ev.name}`} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover group-hover/photo:scale-105 transition-transform duration-500" />
                    <span className="absolute bottom-3 right-3 bg-clara-espresso/80 backdrop-blur-sm rounded-full p-2 text-clara-cream opacity-0 group-hover/photo:opacity-100 transition-opacity">
                      <ZoomIn className="w-4 h-4" />
                    </span>
                  </button>
                )}
                <div className="p-8 lg:p-10 flex flex-col flex-1">
                <h3 className="font-display text-3xl mb-6">{ev.name}</h3>
                <div className="space-y-2 mb-8">
                  <div className="flex items-center gap-3 text-sm text-clara-cream/85">
                    <CalendarDays className="w-4 h-4 text-clara-terracotta shrink-0" strokeWidth={1.5} />
                    <span className="font-data">{ev.dates}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-clara-cream/85">
                    <MapPin className="w-4 h-4 text-clara-terracotta shrink-0" strokeWidth={1.5} />
                    <span className="font-data">{ev.venue}</span>
                  </div>
                  {ev.booth && (
                    <div className="flex items-center gap-3 text-sm text-clara-cream/85" data-testid={`event-booth-${ev.id}`}>
                      <Tag className="w-4 h-4 text-clara-terracotta shrink-0" strokeWidth={1.5} />
                      <span className="font-data">{E.boothLabel}: {ev.booth}</span>
                    </div>
                  )}
                </div>
                <div className="mt-auto">
                  <Link
                    to={`${lp("/contact")}?event=${encodeURIComponent(ev.name)}`}
                    data-testid={`event-book-${ev.id}`}
                    className="inline-flex items-center gap-2 rounded-full bg-clara-terracotta px-6 py-3 text-sm font-semibold text-clara-cream hover:bg-clara-cream hover:text-clara-espresso transition-colors duration-300"
                  >
                    {E.bookMeeting} <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {showPast && past.length > 0 && (
          <div data-testid="past-events">
            <p className="eyebrow text-clara-terracotta mb-6">{E.pastLabel}</p>
            <div className="space-y-px">
              {past.map((ev, i) => (
                <Reveal key={ev.id} delay={i * 0.06}>
                  <div className={`grid ${ev.photo ? "sm:grid-cols-[220px_1fr_auto]" : "sm:grid-cols-[1fr_auto]"} gap-4 sm:gap-6 sm:items-center border-t border-clara-cream/15 py-6`} data-testid={`past-event-${ev.id}`}>
                    {ev.photo && (
                      <button
                        type="button"
                        onClick={() => setZoom({ src: ev.photo, alt: `Clara booth at ${ev.name}` })}
                        data-testid={`past-event-photo-${ev.id}`}
                        aria-label={`Enlarge photo: ${ev.name}`}
                        className="group/photo relative block w-full aspect-[16/9] sm:aspect-auto sm:h-28 overflow-hidden border border-clara-cream/15 cursor-zoom-in"
                      >
                        <img src={ev.photo} alt={`Clara booth at ${ev.name}`} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover group-hover/photo:scale-105 transition-transform duration-500" />
                        <span className="absolute bottom-2 right-2 bg-clara-espresso/80 backdrop-blur-sm rounded-full p-1.5 text-clara-cream opacity-0 group-hover/photo:opacity-100 transition-opacity">
                          <ZoomIn className="w-3.5 h-3.5" />
                        </span>
                      </button>
                    )}
                    <div>
                      <span className="font-display text-2xl lg:text-3xl">{ev.name}</span>
                      <span className="block sm:inline sm:ml-4 font-data text-sm text-clara-darkmuted">{ev.venue}</span>
                    </div>
                    <span className="font-data text-sm text-clara-cream/80">{ev.dates}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Click-to-enlarge lightbox */}
      <AnimatePresence>
        {zoom && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            data-testid="photo-lightbox"
            className="fixed inset-0 z-[100] bg-black/92 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10 cursor-zoom-out"
            onClick={() => setZoom(null)}
            role="dialog"
            aria-label={zoom.alt}
          >
            <motion.img
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.97, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              src={zoom.src}
              alt={zoom.alt}
              className="max-w-full max-h-full object-contain border border-clara-cream/20"
            />
            <button
              type="button"
              onClick={() => setZoom(null)}
              data-testid="lightbox-close"
              aria-label="Close enlarged photo"
              className="absolute top-5 right-5 rounded-full bg-clara-cream/10 border border-clara-cream/25 p-2.5 text-clara-cream hover:bg-clara-terracotta transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
